import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BookingLink } from '@/components/BookingLink';
import { Card } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { ProvenanceNote } from '@/components/Provenance';
import { api } from '@/lib/api';
import type { Recommendation, Trip, TripPlan } from '@shared/types';

function toneFor(action: Recommendation['action']) {
  if (action === 'use' || action === 'book') return 'success' as const;
  if (action === 'skip') return 'line' as const;
  if (action === 'enroll') return 'brass' as const;
  return 'navy' as const;
}

export function TripDetailPage() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [plan, setPlan] = useState<TripPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!tripId) return;
    api
      .trip(tripId)
      .then((data) => {
        setTrip(data.trip);
        setPlan(data.plan);
        setSelected(data.plan.recommendations.slice(0, 2).map((r) => r.serviceId));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Trip failed to load.'));
  }, [tripId]);

  if (error) return <p className="text-sm text-alert">{error}</p>;
  if (!trip || !plan) return <p className="text-sm text-muted">Building the plan…</p>;

  function toggle(id: string) {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <div className="space-y-4">
      <Card>
        <ChipRow>
          <Chip tone="navy">{trip.originIata}</Chip>
          <Chip>→</Chip>
          <Chip tone="navy">{trip.destIata}</Chip>
          <Chip>{trip.airline}</Chip>
          <Chip>{trip.cabin}</Chip>
          {trip.highFriction === 1 && <Chip tone="alert">High friction</Chip>}
          {trip.source === 'google_calendar' && <Chip tone="brass">Calendar</Chip>}
        </ChipRow>
        <h2 className="mt-3 font-display text-[30px] leading-none text-navy">{plan.headline}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted">{plan.summary}</p>
        {trip.source === 'google_calendar' && (
          <p className="mt-2 text-[12px] text-faint">
            source=google_calendar
            {trip.calendarEventId ? ` · event_id=${trip.calendarEventId}` : ''}
            {trip.importedAt ? ` · imported_at=${trip.importedAt}` : ''}
          </p>
        )}
        <ProvenanceNote
          className="mt-2"
          provenance={{ verifiedAt: plan.playbookVerifiedAt, source: 'United_EWR playbook', confidence: plan.confidence }}
        />
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={`/trips/${trip.id}/print`} className="rounded-card bg-navy px-3 py-2.5 text-center text-sm font-semibold text-sand">
            Print / PDF
          </Link>
          <button
            className="rounded-card border border-line px-3 py-2.5 text-sm font-semibold"
            onClick={async () => {
              await api.deleteTrip(trip.id);
              navigate('/trips');
            }}
          >
            Delete
          </button>
        </div>
      </Card>

      {plan.recommendations.map((rec) => (
        <Card key={`${rec.serviceId}-${rec.airportIata}-${rec.headline}`}>
          <ChipRow>
            <Chip tone={toneFor(rec.action)}>{rec.action}</Chip>
            <Chip>{rec.airportIata}</Chip>
            <Chip>{rec.kind}</Chip>
            {rec.covered && <Chip tone="success">Covered</Chip>}
            {!rec.reachable && <Chip tone="alert">Tight clock</Chip>}
          </ChipRow>
          <h3 className="mt-2 font-display text-2xl text-navy">{rec.headline}</h3>
          <p className="mt-2 text-sm font-semibold">{rec.estimatedCost.label}</p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-muted">
            {rec.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          {rec.assumptions.length > 0 && (
            <div className="mt-3 border-t border-line pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-faint">Assumptions</p>
              <ul className="mt-1 space-y-1 text-[12px] text-muted">
                {rec.assumptions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <ProvenanceNote className="mt-2" provenance={rec.provenance} />
          <BookingLink href={rec.bookingUrl} kind={rec.kind} />
          <label className="mt-3 flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={selected.includes(rec.serviceId)} onChange={() => toggle(rec.serviceId)} />
            Include in compare
          </label>
        </Card>
      ))}

      <Card>
        <h3 className="font-display text-2xl text-navy">Booking timeline</h3>
        <ol className="mt-3 space-y-3">
          {plan.timeline.map((item) => (
            <li key={item.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-brass">{item.when}</p>
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="text-[13px] text-muted">{item.detail}</p>
              <BookingLink href={item.bookingUrl} label="Reserve" />
            </li>
          ))}
        </ol>
      </Card>

      <Card>
        <h3 className="font-display text-2xl text-navy">Benefit optimization</h3>
        {plan.optimizations.length === 0 ? (
          <p className="mt-2 text-sm text-muted">No enrollment or upgrade suggestion beyond using what you already have.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {plan.optimizations.map((item) => (
              <li key={item.title}>
                <ChipRow>
                  <Chip tone="brass">{item.kind.replace('_', ' ')}</Chip>
                </ChipRow>
                <p className="mt-1 text-sm font-semibold">{item.title}</p>
                <p className="text-[13px] text-muted">{item.detail}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="font-display text-2xl text-navy">Assumptions on this plan</h3>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-[13px] text-muted">
          {plan.assumptions.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {selected.length >= 2 && (
          <Link
            to={`/compare?ids=${selected.join(',')}`}
            className="mt-4 block rounded-card bg-navy px-4 py-3 text-center text-sm font-semibold text-sand"
          >
            Compare selected services
          </Link>
        )}
      </Card>
    </div>
  );
}
